import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {Alert, Button} from '@betareader/design-system';
import styled from 'styled-components';
import {
  CritiqueCheckpoint,
  CritiqueGroup,
  useRemoveCheckpoint,
} from 'src/hooks/useCritiqueGroups';
import { CheckpointTimeline } from 'src/components/critiqueGroups/checkpointTimeline';
import { CritiqueEmptyState } from 'src/components/critiqueGroups/critiqueStates';
import { MediaQuery } from 'src/constants/size';
import CheckpointEditor from './checkpointEditor';
import { WhosDone } from './whosDone';

/** Design D1 — the host's checkpoint timeline with the who's-done pulse. */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  overflow: hidden;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid #eee7da;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  ${MediaQuery.phone} {
    padding: 14px;
  }
`;

// The timeline's own inset. At 24px+ per side this was the largest single slice
// of a phone's width in the feature, on the tab with the least room to spare.
const Body = styled.div`
  padding: 22px 26px;
  ${MediaQuery.phone} {
    padding: 16px 12px;
  }
`;

const HostCheckpoints: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const removeCheckpoint = useRemoveCheckpoint(group._id);
  const [editing, setEditing] = useState<CritiqueCheckpoint | undefined>();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const checkpoints = group.checkpoints || [];

  const openNew = () => {
    setEditing(undefined);
    setOpen(true);
  };
  const openEdit = (cp: CritiqueCheckpoint) => {
    setEditing(cp);
    setOpen(true);
  };

  // The host's timeline shows GROUP progress, so no per-row `done` is passed —
  // "done" here belongs to members, and the who's-done panel carries it.
  const meta = Object.fromEntries(
    checkpoints.map(cp => [
      cp._id,
      {
        action: (
          <details data-testid={`checkpoint-menu-${cp._id}`}>
            <summary aria-label={t('Actions', 'Actions')} style={{cursor: 'pointer'}}>⋮</summary>
            <div style={{display: 'grid', gap: '.25rem'}}>
              <Button variant='secondary' size='small' onClick={() => openEdit(cp)}>
                {t('Edit', 'Edit')}
              </Button>
              <Button variant='danger' size='small'
                onClick={() =>
                  removeCheckpoint.mutate(
                    { checkpointId: cp._id },
                    { onError: (err: any) => setError(err?.message) }
                  )
                }
                data-testid={`checkpoint-delete-${cp._id}`}
              >
                {t('Delete', 'Delete')}
              </Button>
            </div>
          </details>
        ),
        footer: (
          <>
            <Button
              variant='secondary'
              size='small'
              style={{ borderRadius: 7, marginTop: 10 }}
              onClick={() => setExpanded(e => (e === cp._id ? undefined : cp._id))}
              data-testid={`checkpoint-whos-done-${cp._id}`}
            >
              {expanded === cp._id
                ? t('CritiqueHideWhosDone', 'Hide progress')
                : t('CritiqueWhosDone', 'Who’s done')}
            </Button>
            {expanded === cp._id && (
              <WhosDone groupId={group._id} checkpointId={cp._id} />
            )}
          </>
        ),
      },
    ])
  );

  return (
    <div data-testid="host-checkpoints">
      <Panel>
        <Head>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
              {t('CritiqueReadingCheckpoints', 'Reading checkpoints')}
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, color: '#8a857b' }}>
              {t('CritiqueCheckpointsSubtitle', 'Moving through the book together')}
            </p>
          </div>
          <Button
            size='small'
            onClick={openNew}
            style={{ borderRadius: 8, fontWeight: 700 }}
            data-testid="add-checkpoint-button"
          >
            {t('CritiqueAddCheckpoint', 'Add checkpoint')}
          </Button>
        </Head>

        <Body>
          {error && (
            <Alert variant='error' data-testid="checkpoints-error">
              {error}
            </Alert>
          )}
          <CheckpointTimeline
            checkpoints={checkpoints}
            meta={meta}
            emptyState={
              <CritiqueEmptyState
                data-testid="checkpoints-empty"
                icon="flag checkered"
                title={t('CritiqueNoCheckpointsTitle', 'Set the pace together')}
                body={t(
                  'CritiqueNoCheckpointsBody',
                  'Add a checkpoint — a name and a date — so everyone knows where the circle is heading next.'
                )}
                action={
                  <Button
                    size='small'
                    onClick={openNew}
                    style={{ borderRadius: 9, fontWeight: 700 }}
                    data-testid="add-checkpoint-empty"
                  >
                    {t('CritiqueAddCheckpoint', 'Add checkpoint')}
                  </Button>
                }
              />
            }
          />
        </Body>
      </Panel>

      <CheckpointEditor
        group={group}
        checkpoint={editing}
        open={open}
        onClose={() => setOpen(false)}
      />
    </div>
  );
};

export default HostCheckpoints;
